"use client";

import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import { Menu, X, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { courses as allCourses } from "@/lib/courses";

const courses = allCourses.map((c) => ({
  name: c.name,
  href: `/courses/${c.slug}`,
  badge: c.cert,
  color: c.color,
}));

const navLinks = [
  { label: "Labs", href: "/labs" },
  { label: "Certifications", href: "/certifications" },
  { label: "For Teams", href: "/business" },
  { label: "Blog", href: "/blog" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact-us" },
];

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [coursesOpen, setCoursesOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-cb-border bg-cb-bg/90 backdrop-blur-md">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center">
            <Image
              src="/CloudBreach_logo.png"
              alt="CloudBreach"
              width={180}
              height={45}
              sizes="180px"
              className="h-10 w-auto"
              style={{ mixBlendMode: "screen" }}
              priority
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {/* Courses dropdown */}
            <div className="relative">
              <Link
                href="/courses"
                className={cn(
                  "flex items-center gap-1 px-3 py-2 rounded-md text-base font-medium transition-colors",
                  "text-cb-muted hover:text-cb-text hover:bg-cb-surface-2"
                )}
                onMouseEnter={() => setCoursesOpen(true)}
                onMouseLeave={() => setCoursesOpen(false)}
              >
                Courses
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 transition-transform duration-150",
                    coursesOpen && "rotate-180"
                  )}
                />
              </Link>
              {coursesOpen && (
                <div
                  className="absolute left-0 top-full pt-1"
                  onMouseEnter={() => setCoursesOpen(true)}
                  onMouseLeave={() => setCoursesOpen(false)}
                >
                  <div className="w-64 rounded-xl border border-cb-border bg-cb-surface p-1.5 shadow-xl shadow-black/40">
                    {courses.map((course) => (
                      <Link
                        key={course.href}
                        href={course.href}
                        className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-cb-muted hover:text-cb-text hover:bg-cb-surface-2 transition-colors"
                      >
                        <span>{course.name}</span>
                        <span
                          className="text-xs font-mono px-1.5 py-0.5 rounded"
                          style={{
                            color: course.color,
                            backgroundColor: `${course.color}18`,
                          }}
                        >
                          {course.badge}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="px-3 py-2 rounded-md text-base font-medium text-cb-muted hover:text-cb-text hover:bg-cb-surface-2 transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://portal.cloudbreach.io/sign-in/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-base text-cb-muted hover:text-cb-text border border-cb-border hover:border-cb-violet/40 px-4 py-2 rounded-md transition-colors"
            >
              Sign in
            </a>
            <Button asChild size="md">
              <Link href="/courses" className="flex items-center gap-1.5">
                Start Training
                <ChevronRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden flex h-11 w-11 items-center justify-center rounded-md text-cb-muted hover:text-cb-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cb-violet/50"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-cb-border py-4 space-y-1">
            <div className="px-2 pb-2">
              <p className="px-3 py-1 text-sm font-semibold text-cb-muted-2 uppercase tracking-wider">
                Courses
              </p>
              {courses.map((course) => (
                <Link
                  key={course.href}
                  href={course.href}
                  className="flex items-center justify-between px-3 py-2.5 rounded-lg text-sm text-cb-muted hover:text-cb-text hover:bg-cb-surface-2 transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  <span>{course.name}</span>
                  <span
                    className="text-xs font-mono px-1.5 py-0.5 rounded"
                    style={{
                      color: course.color,
                      backgroundColor: `${course.color}18`,
                    }}
                  >
                    {course.badge}
                  </span>
                </Link>
              ))}
            </div>
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="block px-3 py-2.5 rounded-lg text-sm text-cb-muted hover:text-cb-text hover:bg-cb-surface-2 transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-3 px-2 border-t border-cb-border mt-3 space-y-2">
              <a
                href="https://portal.cloudbreach.io/sign-in/"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center px-4 py-2.5 rounded-lg text-sm text-cb-muted border border-cb-border hover:border-cb-violet/40 hover:text-cb-text transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                Sign in
              </a>
              <Button className="w-full" asChild>
                <Link href="/courses" className="flex items-center gap-1.5">
                  Start Training
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
